import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, Map, Users, Settings, LogOut, HardHat, Globe, Menu, X, DollarSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { isAdmin, canEditMechanicPrices } from "@/lib/roles";

const NavItem = ({ to, icon: Icon, label, testid, onClick }) => (
  <NavLink
    to={to}
    end={to === "/"}
    onClick={onClick}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
        isActive
          ? "bg-[var(--brand)] text-white shadow-sm"
          : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon className="h-[18px] w-[18px]" />
    <span>{label}</span>
  </NavLink>
);

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const admin = isAdmin(user);
  const showPricing = canEditMechanicPrices(user);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const roleLabel = admin ? t("super_admin") : `${t(user?.role || "")} ${t("engineer")}`;
  const close = () => setOpen(false);

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col border-e border-slate-200 bg-white">
      <div className="flex items-center gap-2.5 px-6 py-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand-light)]">
          <HardHat className="h-6 w-6 text-[var(--brand)]" />
        </div>
        <div>
          <div className="text-sm font-bold leading-tight text-slate-900">{t("construction")}</div>
          <div className="text-xs leading-tight text-[var(--brand)]">{t("estimation_system")}</div>
        </div>
      </div>

      <nav className="flex flex-1 flex-col gap-1 px-3">
        <NavItem to="/" icon={LayoutGrid} label={t("dashboard")} testid="nav-dashboard" onClick={close} />
        <NavItem to="/units" icon={Map} label={t("units")} testid="nav-units" onClick={close} />
        {showPricing ? <NavItem to="/mechanic-pricing" icon={DollarSign} label={t("mechanic_pricing")} testid="nav-mechanic-pricing" onClick={close} /> : null}
        {admin ? <NavItem to="/users" icon={Users} label={t("users")} testid="nav-users" onClick={close} /> : null}
        <NavItem to="/settings" icon={Settings} label={t("settings")} testid="nav-settings" onClick={close} />
      </nav>

      <div className="border-t border-slate-100 p-3">
        <div className="mb-2 flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand-light)] text-sm font-bold text-[var(--brand)]">
            {(user?.name || "U")[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-slate-900">{user?.name}</div>
            <div className="truncate text-xs text-slate-400">{roleLabel}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100"
        >
          <LogOut className="h-4 w-4" /> {t("sign_out")}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-[var(--bg)]">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">{Sidebar}</div>

      {/* Mobile drawer */}
      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="absolute inset-y-0 start-0 z-50">{Sidebar}</div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur lg:px-8">
          <div className="flex items-center gap-3">
            <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <span className="text-sm font-medium text-slate-500">
              {t("welcome_back")} <span className="font-semibold text-slate-900">{user?.name}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="lang-toggle-btn"
              onClick={() => setLanguage(language === "en" ? "ku" : "en")}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              <Globe className="h-3.5 w-3.5" />
              {language === "en" ? "EN" : "کوردی"}
            </button>
            <span className="rounded-full bg-[var(--brand-light)] px-3 py-1.5 text-xs font-semibold text-[var(--brand)]">
              {roleLabel}
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-4 py-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
